import classNames from "classnames";
import { useContext, useMemo } from "react";
import { CloseIcon } from "@icons";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { ALLOWED_FINANCIAL_ACCOUNT_TYPES_FOR_FILE_IMPORT } from "@common/constants";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { GridTableRow } from "@design-system/Tables/GridTable/GridTable";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { DocumentName } from "../documents/DocumentName";
import { EditableTableCell } from "../accounting/editableTable/EditableTableCell";
import editableTableStyles from "../accounting/editableTable/EditableTable.module.scss";
import { EntitySelect } from "../entity/EntitySelect";
import { EditableTableCellString } from "../accounting/editableTable/EditableTableCellString";
import { FinancialAccountFinancialTypeSelect } from "../financialAccount/FinancialAccountFinancialTypeSelect";
import { useRemoveImporterDocument } from "./useRemoveImporterDocument";
import { ImporterDocumentStatus } from "./ImporterDocumentStatus";
import styles from "./ImporterPage.module.scss";
import { ImporterDocumentRow } from "./types";
import { ImporterContext } from "./importer.context";
import { ImporterAccountPicker } from "./ImporterAccountPicker";
import { ImporterStatementDateCell } from "./ImporterStatementDateCell";
import { useImporterAccountEditor } from "./useImporterAccountEditor";
import { getOpeningDatePresets } from "./openingDatePresets";

interface Props {
    rowNumber: number;
    documentRow: ImporterDocumentRow;
    financialDocument: FinancialDocumentExtended;
    integrationAccount?: IntegrationAccount;
}

export const ImporterTableAccountRow: React.FC<Props> = ({
    rowNumber,
    documentRow,
    financialDocument,
    integrationAccount,
}) => {
    const { setPreviewedRowId, previewedRowId, documentRows } =
        useContext(ImporterContext);
    const openingDatePresets = useMemo(
        () =>
            getOpeningDatePresets({
                statement: financialDocument,
                statements: documentRows.flatMap(
                    (row) => row.financialDocument ?? [],
                ),
            }),
        [financialDocument, documentRows],
    );
    const isPreviewed = previewedRowId === documentRow.id;

    const {
        entities,
        selectedFinancialAccount,
        selectedEntity,
        isAccountLinked,
        institutionName,
        financialAccountType,
        isReadOnly,
        disabledReason,
        accountPickerDisabledReason,
        updateAccountInfoField,
        onFinancialAccountSelected,
        onInstitutionNameChanged,
        onAccountTypeChanged,
        onEntityChanged,
    } = useImporterAccountEditor({
        integrationAccount,
        financialDocument,
        documentRowId: documentRow.id,
        shouldAutoAssignEntity: true,
    });

    const { removeDocument } = useRemoveImporterDocument(documentRow);

    return (
        <GridTableRow
            columnBorders
            className={classNames(styles.row, {
                [styles.previewedRow]: isPreviewed,
            })}
            onClick={(e) => e.stopPropagation()}
            clickable={false}
        >
            <GridTableRow columnBorders className={styles.stickyLeft}>
                <div className={styles.rowNumber}>{rowNumber}</div>
                <div>
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setPreviewedRowId(documentRow.id)}
                        className={styles.documentNameButton}
                    >
                        <DocumentName
                            fileName={financialDocument.fileName}
                            documentId={financialDocument.id}
                        />
                    </Button>
                </div>
            </GridTableRow>
            <ImporterDocumentStatus documentRow={documentRow} />
            <EditableTableCellString
                persistOnBlur
                value={institutionName ?? ""}
                onChange={onInstitutionNameChanged}
                className={classNames(styles.editableCell, {
                    [styles.readOnlyCell]: isReadOnly,
                })}
                readOnly={isReadOnly}
                disabledReason={disabledReason}
            />
            <EditableTableCell
                className={classNames(
                    editableTableStyles.dropdownEditableCell,
                    styles.editableCell,
                )}
            >
                <ImporterAccountPicker
                    accountInfoExtraction={
                        financialDocument.accountInfoExtraction
                    }
                    selectedFinancialAccount={selectedFinancialAccount}
                    entityId={selectedEntity?.id}
                    onAccountInfoChanged={updateAccountInfoField}
                    onFinancialAccountSelected={onFinancialAccountSelected}
                    isAccountLinked={isAccountLinked}
                    disabledReason={accountPickerDisabledReason}
                />
            </EditableTableCell>
            <EditableTableCell
                className={classNames(
                    editableTableStyles.dropdownEditableCell,
                    styles.editableCell,
                    styles.baseSelectEditableCell,
                    { [styles.readOnlyCell]: isReadOnly },
                )}
            >
                <FinancialAccountFinancialTypeSelect
                    value={financialAccountType ?? undefined}
                    onChange={onAccountTypeChanged}
                    allowedTypes={
                        ALLOWED_FINANCIAL_ACCOUNT_TYPES_FOR_FILE_IMPORT
                    }
                    disabledReason={disabledReason}
                />
            </EditableTableCell>
            <ImporterStatementDateCell
                financialDocument={financialDocument}
                field="openingDate"
                presets={openingDatePresets}
            />
            <ImporterStatementDateCell
                financialDocument={financialDocument}
                field="closingDate"
            />
            <EditableTableCell
                className={classNames(
                    editableTableStyles.dropdownEditableCell,
                    styles.editableCell,
                    styles.baseSelectEditableCell,
                )}
            >
                <EntitySelect
                    entities={entities}
                    selectedEntity={selectedEntity}
                    onChange={onEntityChanged}
                    disabledReason={disabledReason}
                    triggerButtonProps={{
                        variant: "primary",
                        block: true,
                    }}
                />
            </EditableTableCell>
            <GridTableRow columnBorders className={styles.stickyRight}>
                <div className={styles.actionsColumn}>
                    {documentRow.financialDocument?.numTransactions}

                    <div className={styles.documentActions}>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setPreviewedRowId(documentRow.id)}
                        >
                            Review
                        </Button>
                        <Tooltip content="Remove document">
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                onClick={removeDocument}
                                aria-label="Remove document"
                            >
                                <CloseIcon />
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </GridTableRow>
        </GridTableRow>
    );
};
